import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Sparkles, Loader2, Edit2, Save, X, AlertCircle, RefreshCw } from 'lucide-react';
import { reachPlanAPI, ReachAdoptionPlan, ReachPlanSection } from '../../services/reachService';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const POLL_MS = 5000;
const TIMEOUT_MS = 120_000;

export const AdoptionPlanPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [plan, setPlan] = useState<ReachAdoptionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // requestId → sectionId (null = full plan). Tracks in-flight tasks.
  const [activeTasks, setActiveTasks] = useState<Record<string, string | null>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const pollTimers = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const taskStartTimes = useRef<Record<string, number>>({});

  const load = useCallback(async () => {
    if (!projectId) return;
    const r = await reachPlanAPI.get(projectId);
    setPlan(r.data.plan);
  }, [projectId]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    const timers = pollTimers.current;
    return () => { Object.values(timers).forEach(clearInterval); };
  }, []);

  const stopPolling = useCallback((requestId: string) => {
    if (pollTimers.current[requestId]) {
      clearInterval(pollTimers.current[requestId]);
      delete pollTimers.current[requestId];
    }
    delete taskStartTimes.current[requestId];
    setActiveTasks(prev => {
      const next = { ...prev };
      delete next[requestId];
      return next;
    });
  }, []);

  const startPolling = useCallback((requestId: string, sectionId: string | null) => {
    if (!projectId) return;
    taskStartTimes.current[requestId] = Date.now();
    setActiveTasks(prev => ({ ...prev, [requestId]: sectionId }));

    pollTimers.current[requestId] = setInterval(async () => {
      const elapsed = Date.now() - (taskStartTimes.current[requestId] ?? 0);
      if (elapsed > TIMEOUT_MS) {
        stopPolling(requestId);
        toast.error('Plan generation timed out. Try again.');
        return;
      }
      try {
        const r = await reachPlanAPI.getTask(projectId, requestId);
        if (r.data.status === 'complete') {
          stopPolling(requestId);
          await load();
        } else if (r.data.status === 'failed') {
          stopPolling(requestId);
          toast.error(r.data.error ?? 'Plan generation failed.');
        }
      } catch {
        // transient network error — keep polling
      }
    }, POLL_MS);
  }, [projectId, load, stopPolling]);

  const generate = async (sectionId?: string) => {
    if (!projectId) return;
    if (!sectionId) setGenerating(true);
    try {
      const r = await reachPlanAPI.generate(projectId, sectionId);
      startPolling(r.data.requestId, sectionId ?? null);
    } catch {
      toast.error('Failed to start plan generation.');
    } finally {
      if (!sectionId) setGenerating(false);
    }
  };

  const startEdit = (section: ReachPlanSection) => {
    setEditingId(section.id);
    setEditDraft(section.editedContent ?? section.content ?? '');
  };

  const saveEdit = async (sectionId: string) => {
    if (!projectId) return;
    setSaving(true);
    try {
      await reachPlanAPI.updateSection(projectId, sectionId, editDraft);
      setPlan(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map(s =>
            s.id === sectionId ? { ...s, editedContent: editDraft } : s
          )
        };
      });
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  if (!projectId) return null;

  const anyFullPlanGenerating = Object.values(activeTasks).some(v => v === null);
  const isSectionGenerating = (sectionId: string) =>
    Object.values(activeTasks).some(v => v === sectionId);

  const renderSectionBody = (section: ReachPlanSection, isEditing: boolean) => {
    const display = section.editedContent ?? section.content;
    if (isEditing) {
      return (
        <textarea
          rows={8}
          value={editDraft}
          onChange={e => setEditDraft(e.target.value)}
          className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
        />
      );
    }
    if (display) {
      return (
        <div className="prose prose-sm max-w-none prose-headings:text-navy prose-headings:font-bold prose-p:text-slate-700 prose-p:leading-relaxed prose-a:text-primary prose-strong:text-navy prose-table:text-sm prose-th:text-navy prose-th:font-semibold prose-th:bg-slate-50 prose-td:text-slate-700 prose-li:text-slate-700">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{display}</ReactMarkdown>
        </div>
      );
    }
    const sectionPending = isSectionGenerating(section.id) || anyFullPlanGenerating;
    return (
      <div className="flex items-center gap-2 py-4 text-slate-400">
        {sectionPending
          ? <><Loader2 size={14} className="animate-spin" /><span className="text-sm">Generating…</span></>
          : <><AlertCircle size={14} /><span className="text-sm">Not yet generated.</span></>
        }
        {section.guidance && !sectionPending && (
          <p className="ml-2 text-xs text-slate-400 italic">{section.guidance}</p>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <FileText size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Change Adoption Plan</h1>
            <p className="text-slate-500 text-sm font-medium">
              {plan
                ? `${plan.sections.filter(s => s.content || s.editedContent).length} of ${plan.sections.length} sections generated`
                : 'AI-generated plan using your REACH engagement data'}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {anyFullPlanGenerating && (
              <span className="flex items-center gap-1.5 text-xs text-amber-600 font-semibold">
                <Loader2 size={13} className="animate-spin" /> Generating…
              </span>
            )}
            <button
              onClick={() => generate()}
              disabled={generating || anyFullPlanGenerating}
              className="flex items-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60"
            >
              {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
              {generating ? 'Queuing…' : 'Generate plan'}
            </button>
          </div>
        </header>

        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-slate-300" />
          </div>
        )}

        {!loading && (!plan || plan.sections.length === 0) && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FileText size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No plan generated yet.</p>
            <p className="text-slate-400 text-xs mt-1">Click "Generate plan" to produce a Change Adoption Plan from your engagement data.</p>
          </div>
        )}

        {!loading && plan && plan.sections.length > 0 && (
          <div className="flex flex-col gap-4">
            {plan.sections.map(section => {
              const isEditing = editingId === section.id;
              const isRegening = isSectionGenerating(section.id);
              const display = section.editedContent ?? section.content;

              return (
                <div key={section.id} className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#e2e8f0] bg-slate-50/50 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-bold text-navy truncate">{section.title}</p>
                      {!!section.editedContent && (
                        <span className="flex-shrink-0 flex items-center gap-1 text-[10px] text-primary font-semibold">
                          <Edit2 size={10} /> Edited
                        </span>
                      )}
                      {!section.hasSufficientData && (
                        <span className="flex-shrink-0 flex items-center gap-1 text-[10px] text-amber-600 font-semibold">
                          <AlertCircle size={10} /> Low data
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {display && !isEditing && (
                        <button
                          onClick={() => startEdit(section)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-colors"
                        >
                          <Edit2 size={12} /> Edit
                        </button>
                      )}
                      {isEditing && (
                        <>
                          <button
                            onClick={() => saveEdit(section.id)}
                            disabled={saving}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60"
                          >
                            <Save size={12} /> {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-colors"
                          >
                            <X size={12} /> Cancel
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => generate(section.id)}
                        disabled={isRegening || anyFullPlanGenerating}
                        title="Regenerate this section"
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-colors disabled:opacity-40"
                      >
                        {isRegening ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                      </button>
                    </div>
                  </div>

                  <div className="p-4">
                    {renderSectionBody(section, isEditing)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdoptionPlanPage;
